
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/enhanced/button';
import { Separator } from '@/components/ui/separator';
import { Booking } from '@/types/booking';
import { Calendar, Clock, Mail, Phone, Pencil, Trash } from 'lucide-react';
import { toast } from 'sonner';

interface BookingDetailsDialogProps {
  booking: Booking;
  onDelete: (bookingId: string) => void;
}

const BookingDetailsDialog: React.FC<BookingDetailsDialogProps> = ({
  booking,
  onDelete,
}) => {

  const formatDate = (date: Date) => {
    return format(date, 'dd.MM.yyyy', { locale: nb });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Booking detaljer</DialogTitle>
        <DialogDescription>Detaljer om booking fra {booking.companyName}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-6 py-4">
        <div>
          <h3 className="text-lg font-medium">Bedriftsinformasjon</h3>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Bedrift</p>
              <p>{booking.companyName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Kontaktperson</p>
              <p>{booking.contactName}</p>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <p>{booking.contactEmail}</p>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <p>{booking.contactPhone}</p>
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-medium">Bookingdetaljer</h3>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <div>
                <p className="text-sm font-medium text-gray-500">Periode</p>
                <p>{formatDate(booking.startDate)} - {formatDate(booking.endDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  booking.status === 'aktiv' ? 'bg-green-100 text-green-800' : 
                  booking.status === 'planlagt' ? 'bg-blue-100 text-blue-800' : 
                  booking.status === 'fullført' ? 'bg-gray-100 text-gray-800' : 
                  'bg-red-100 text-red-800'
                }`}>
                  {booking.status}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pris</p>
              <p className="font-medium">{formatPrice(booking.price)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Plassering</p>
              <p>{booking.placement === 'top' ? 'Topp 10' : 'Standard'}</p>
            </div>
          </div>
        </div>

        {booking.notes && (
          <>
            <Separator />
            <div>
              <h3 className="text-lg font-medium">Notater</h3>
              <p className="mt-2 text-gray-700 whitespace-pre-line">{booking.notes}</p>
            </div>
          </>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" size="sm" onClick={() => toast.info('Redigering ikke implementert enda')}>
          <Pencil className="h-4 w-4 mr-2" />
          Rediger
        </Button>
        <Button 
          variant="destructive" 
          size="sm"
          onClick={() => {
            if (window.confirm('Er du sikker på at du vil slette denne bookingen?')) {
              onDelete(booking.id);
            }
          }}
        >
          <Trash className="h-4 w-4 mr-2" />
          Slett
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default BookingDetailsDialog;
